// src/components/agency/SystemStats.tsx
import React, { useState, useEffect } from 'react';
import {
  Activity,
  Server,
  Users,
  BarChart3,
  Database,
  Shield,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import { FirebaseService } from '../../services/firebase';

interface SystemStatsData {
  totalAgencies: number;
  activeAgencies: number;
  totalUsers: number;
  totalSimulations: number;
}

interface RecentActivity {
  id: string;
  agencyId: string;
  action: string;
  timestamp: any;
  userAgent?: string;
}

export const SystemStats: React.FC = () => {
  const [stats, setStats] = useState<SystemStatsData | null>(null);
  const [recentLogs, setRecentLogs] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      setError(null);

      // システム統計を取得
      const systemStats = await FirebaseService.getSystemStats();
      setStats({
        ...systemStats,
        totalUsers: 0, // TODO: 実装時に適切な値を設定
        totalSimulations: 0, // TODO: 実装時に適切な値を設定
      });

      // 最近のアクセスログを取得
      const logs = await FirebaseService.getRecentAccessLogs();
      setRecentLogs(logs.slice(0, 10)); // 最新10件
    } catch (err) {
      setError(err instanceof Error ? err.message : 'システム統計の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '未設定';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'login':
        return 'ログイン';
      case 'simulation':
        return 'シミュレーション';
      case 'pdf_export':
        return 'PDF出力';
      default:
        return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'login':
        return 'bg-blue-100 text-blue-800';
      case 'simulation':
        return 'bg-green-100 text-green-800';
      case 'pdf_export':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">システム統計を読み込んでいます...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">エラーが発生しました</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadSystemData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              再試行
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">システム監視</h2>
          <p className="text-gray-600 mt-1">システム全体の利用状況とパフォーマンスを監視</p>
        </div>
        <button
          onClick={loadSystemData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>更新</span>
        </button>
      </div>

      {/* システムステータス */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">総代理店数</p>
              <p className="text-3xl font-bold text-blue-900">{stats?.totalAgencies || 0}</p>
            </div>
            <Server className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">アクティブ代理店</p>
              <p className="text-3xl font-bold text-green-900">{stats?.activeAgencies || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">総ユーザー数</p>
              <p className="text-3xl font-bold text-purple-900">{stats?.totalUsers || 0}</p>
            </div>
            <Users className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">総シミュレーション</p>
              <p className="text-3xl font-bold text-orange-900">{stats?.totalSimulations || 0}</p>
            </div>
            <BarChart3 className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* システムヘルス */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Shield className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">システムヘルス</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Firebase Authentication</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center space-x-1">
                <CheckCircle className="w-3 h-3" />
                <span>正常</span>
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-600">Firestore Database</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center space-x-1">
                <CheckCircle className="w-3 h-3" />
                <span>正常</span>
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-600">Cloud Functions</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center space-x-1">
                <CheckCircle className="w-3 h-3" />
                <span>正常</span>
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-600">システム負荷</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">低</span>
            </div>
          </div>

          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-medium">すべてのサービスが正常に動作しています</span>
            </div>
          </div>
        </div>

        {/* 利用状況 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-6">
            <TrendingUp className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">利用状況サマリー</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">代理店稼働率</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 rounded-full h-2"
                    style={{ width: `${stats ? (stats.activeAgencies / stats.totalAgencies) * 100 : 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {stats ? Math.round((stats.activeAgencies / stats.totalAgencies) * 100) : 0}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-600">平均ユーザー/代理店</span>
              <span className="font-medium text-gray-900">
                {stats && stats.activeAgencies > 0 ? Math.round(stats.totalUsers / stats.activeAgencies) : 0}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-600">平均シミュレーション/ユーザー</span>
              <span className="font-medium text-gray-900">
                {stats && stats.totalUsers > 0 ? Math.round(stats.totalSimulations / stats.totalUsers) : 0}
              </span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-xl font-bold text-blue-900">
                {stats ? Math.round(stats.totalSimulations / Math.max(stats.totalAgencies, 1)) : 0}
              </div>
              <div className="text-xs text-blue-600">代理店別平均利用</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-xl font-bold text-purple-900">
                {stats ? (stats.totalUsers / Math.max(stats.totalAgencies, 1)).toFixed(1) : '0.0'}
              </div>
              <div className="text-xs text-purple-600">代理店別平均ユーザー</div>
            </div>
          </div>
        </div>

        {/* 最近のアクティビティ */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 lg:col-span-2">
          <div className="flex items-center space-x-2 mb-6">
            <Activity className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">最近のアクティビティ</h3>
          </div>

          {recentLogs.length > 0 ? (
            <div className="space-y-3">
              {recentLogs.map((log) => (
                <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                      {getActionLabel(log.action)}
                    </span>
                    <span className="text-sm text-gray-600">代理店: {log.agencyId}</span>
                  </div>
                  <span className="text-sm text-gray-500">{formatDate(log.timestamp)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">最近のアクティビティがありません</p>
            </div>
          )}
        </div>
      </div>

      {/* システム設定 */}
      <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Database className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">システム設定</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Firebase プロジェクト</h4>
            <p className="text-sm text-gray-600">smile-simulator</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">リージョン</h4>
            <p className="text-sm text-gray-600">asia-northeast1 (東京)</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">環境</h4>
            <p className="text-sm text-gray-600">本番環境</p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">📊 監視ポイント</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Firebase使用量: 無料枠内で運用中</li>
            <li>• セキュリティルール: 最新の設定が適用済み</li>
            <li>• バックアップ: 自動バックアップが有効</li>
            <li>• パフォーマンス: 平均応答時間 &lt; 3秒</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
